import { useCallback, useMemo } from 'react';
import { useAuthContext, useCalendarContext } from '../context';
import { useUpdateCalendarPref, useUpdateMealPref, useUpdateWorkoutPref } from '../mutations';
import { usePreferences } from '../queries';
import { CalendarPreference, MealPreference } from '@demind-inc/core';
import { debounce, pick } from 'lodash';
import {
  AssistantConfigEntireDay,
  AssistantConfigMeal,
  AssistantConfigSleep,
  AssistantConfigTask,
  AssistantConfigWorkout,
} from '../types';

type MealPreferenceUpdate = Partial<MealPreference & { note?: string }>;

export const useAssistantPref = () => {
  const { user } = useAuthContext();
  const {
    mealPreferences,
    workoutPreferences,
    calendarPreferences,
    isLoading: isPreferenceLoading,
  } = usePreferences({
    preferenceId: user.preferenceId!,
  });
  const { mainCalendar } = useCalendarContext();
  const { updateCalendarPref: updateCalendarPrefMutation } = useUpdateCalendarPref();
  const { updateMealPref: updateMealPrefMutation } = useUpdateMealPref();
  const { updateWorkoutPref: updateWorkoutPrefMutation } = useUpdateWorkoutPref();

  const sleepPref = useMemo(() => {
    return {
      defaultCalendarId: mainCalendar?.calendarId ?? '',
      unSyncToCalendar: calendarPreferences.unSyncToCalendar?.sleep ?? false,
    };
  }, [mainCalendar, calendarPreferences.unSyncToCalendar?.sleep]);

  const mealPref = useMemo(() => {
    return {
      ...mealPreferences,
      defaultCalendarId: mainCalendar?.calendarId ?? '',
      unSyncToCalendar: calendarPreferences.unSyncToCalendar?.meal ?? false,
    };
  }, [mainCalendar, calendarPreferences.unSyncToCalendar?.meal, mealPreferences]);

  const workoutPref = useMemo(() => {
    return {
      ...workoutPreferences,
      defaultCalendarId: mainCalendar?.calendarId ?? '',
    };
  }, [mainCalendar, workoutPreferences]);

  const taskPref = useMemo(() => {
    return {
      defaultCalendarId: mainCalendar?.calendarId ?? '',
      unSyncToCalendar: calendarPreferences.unSyncToCalendar?.task ?? false,
    };
  }, [mainCalendar, calendarPreferences.unSyncToCalendar?.task]);

  const entireDayPref = useMemo(() => {
    return {
      defaultCalendarId: mainCalendar?.calendarId ?? '',
      unSyncToCalendar: calendarPreferences.unSyncToCalendar?.aiScheduler ?? false,
      transparency: calendarPreferences.transparency?.aiScheduler,
    };
  }, [
    mainCalendar,
    calendarPreferences.unSyncToCalendar?.aiScheduler,
    calendarPreferences.transparency?.aiScheduler,
  ]);

  const debouncedUpdateCalendarSettings = useCallback(
    debounce(
      (newData: Partial<CalendarPreference>) =>
        updateCalendarPrefMutation({
          preferenceId: user.preferenceId!,
          preferences: { ...newData },
        }),
      1000
    ),
    [user.preferenceId]
  );

  const debouncedUpdateMealSettings = useCallback(
    debounce(
      (newData: MealPreferenceUpdate) =>
        updateMealPrefMutation({
          preferenceId: user.preferenceId!,
          preferences: { ...newData },
          routineId: user.routineId!,
        }),
      1000
    ),
    [user.preferenceId, user.routineId]
  );

  const debouncedUpdateWorkoutSettings = useCallback(
    debounce(
      (newData: Record<string, unknown>) =>
        updateWorkoutPrefMutation({
          preferenceId: user.preferenceId!,
          preferences: { ...newData },
        }),
      1000
    ),
    [user.preferenceId]
  );

  // Update Sleep Pref
  const updateSleepPref = useCallback(
    async (params: Partial<AssistantConfigSleep>) => {
      if ('unSyncToCalendar' in params) {
        debouncedUpdateCalendarSettings({
          unSyncToCalendar: {
            ...calendarPreferences.unSyncToCalendar,
            sleep: params.unSyncToCalendar,
          },
        });
      }
    },
    [debouncedUpdateCalendarSettings, calendarPreferences.unSyncToCalendar]
  );

  // Update Task Pref
  const updateTaskPref = useCallback(
    async (params: Partial<AssistantConfigTask>) => {
      if ('unSyncToCalendar' in params) {
        debouncedUpdateCalendarSettings({
          unSyncToCalendar: {
            ...calendarPreferences.unSyncToCalendar,
            task: params.unSyncToCalendar,
          },
        });
      }
    },
    [debouncedUpdateCalendarSettings, calendarPreferences.unSyncToCalendar]
  );

  // Update Meal Pref
  const updateMealPref = useCallback(
    async (params: Partial<AssistantConfigMeal>) => {
      const updates = [];
      if ('unSyncToCalendar' in params) {
        updates.push(
          debouncedUpdateCalendarSettings({
            unSyncToCalendar: {
              ...calendarPreferences.unSyncToCalendar,
              meal: params.unSyncToCalendar,
            },
          })
        );
      }
      // Check for meal fields
      const mealFields = ['breakfast', 'lunch', 'dinner', 'disableAutoSchedule', 'note', 'excludePlanContents'] as const;
      if (mealFields.some((field) => field in params)) {
        const pickedParams = pick(params, mealFields) as MealPreferenceUpdate;
        // Only send changed fields to avoid clobbering concurrent edits
        updates.push(debouncedUpdateMealSettings(pickedParams));
      }
      await Promise.all(updates);
    },
    [
      updateCalendarPrefMutation,
      debouncedUpdateMealSettings,
      user.preferenceId,
      user.routineId,
      calendarPreferences.unSyncToCalendar,
    ]
  );

  // Update Workout Pref
  const updateWorkoutPref = useCallback(
    async (params: Partial<AssistantConfigWorkout>) => {
      const workoutFields = ['note', 'disableAutoSchedule', 'excludePlanContents'] as const;
      if (workoutFields.some((field) => field in params)) {
        const pickedParams = pick(params, workoutFields) as Record<string, unknown>;
        // Only send changed fields to avoid clobbering concurrent edits
        await debouncedUpdateWorkoutSettings(pickedParams);
      }
    },
    [debouncedUpdateWorkoutSettings]
  );

  // Update Entire Day Pref
  const updateEntireDayPref = useCallback(
    async (params: Partial<AssistantConfigEntireDay>) => {
      const updates: Partial<CalendarPreference> = {};
      if ('unSyncToCalendar' in params) {
        updates.unSyncToCalendar = {
          ...calendarPreferences.unSyncToCalendar,
          aiScheduler: params.unSyncToCalendar,
        };
      }
      if ('transparency' in params) {
        updates.transparency = {
          ...calendarPreferences.transparency,
          aiScheduler: params.transparency,
        };
      }
      if (Object.keys(updates).length > 0) {
        debouncedUpdateCalendarSettings(updates);
      }
    },
    [
      debouncedUpdateCalendarSettings,
      calendarPreferences.unSyncToCalendar,
      calendarPreferences.transparency,
    ]
  );

  return {
    isPreferenceLoading,
    sleepPref,
    mealPref,
    workoutPref,
    taskPref,
    entireDayPref,
    defaultCalendar: mainCalendar,
    updateSleepPref,
    updateTaskPref,
    updateMealPref,
    updateWorkoutPref,
    updateEntireDayPref,
  };
};
